<template>
  <div class="gc-wo-bulk-update-material-config">
    <div class="config-card">
      <div class="config-card-header">
        <span class="config-card-title">Material Update Operation</span>
        <span class="config-card-title-secondary"
          >Based on material search criteria</span
        >
      </div>
      <div class="config-card-body">
        <span
          v-if="validated && !selectedOperation"
          class="param-error param-error-top"
        >
          Please select a material update operation.
        </span>

        <div class="operation-section">
          <div class="operation-section-header" v-if="selectedOperation">
            <DxButton
              text="Clear"
              type="default"
              hint="Clear selection"
              @click="clearOperation"
              styling-mode="text"
              class="operation-clear-btn"
            />
          </div>

          <div :key="opListKey" class="operation-list">
            <div
              v-for="op in materialOperations"
              :key="op.value"
              :class="[
                'operation-item',
                { 'op-active': selectedOperation === op.value },
              ]"
            >
              <!-- Radio header row -->
              <label class="op-header">
                <input
                  type="radio"
                  :value="op.value"
                  :checked="selectedOperation === op.value"
                  :disabled="isReadOnly"
                  class="op-radio"
                  @change="onOperationChange(op.value)"
                />
                <div class="op-text">
                  <span class="op-label">{{ op.label }}</span>
                  <span class="op-desc">{{ op.description }}</span>
                </div>
              </label>

              <!-- Inline param form — expands when this op is selected -->
              <div
                v-if="selectedOperation === op.value"
                class="op-params-inline"
              >
                <!-- UpdateQtyAll -->
                <template v-if="op.value === 'UpdateQtyAll'">
                  <div v-if="quantityRange" class="qty-range-context">
                    <span class="qty-range-label"
                      >CURRENT UNIT QTY ACROSS MATCHED WO LINES</span
                    >
                    <div class="qty-range-stats">
                      <div class="qty-stat-box">
                        <span class="qty-stat-key">Min</span>
                        <span class="qty-stat-val">{{
                          getFormattedQty(quantityRange.min)
                        }}</span>
                      </div>
                      <div class="qty-stat-box">
                        <span class="qty-stat-key">Max</span>
                        <span class="qty-stat-val">{{
                          getFormattedQty(quantityRange.max)
                        }}</span>
                      </div>
                      <div class="qty-stat-box">
                        <span class="qty-stat-key">Avg</span>
                        <span class="qty-stat-val">{{
                          getFormattedQty(quantityRange.avg)
                        }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-if="qtyAllRows.length > 0" class="replacement-header">
                    <span class="rt-col-qty"
                      >Current Unit Qty
                      <span class="qty-optional"
                        >(optional — exact match)</span
                      ></span
                    >
                    <span class="rt-col-qty"
                      >New Unit Qty
                      <span class="required-mark">*</span></span
                    >
                    <span class="rt-col-del"></span>
                  </div>
                  <div
                    v-for="(row, i) in qtyAllRows"
                    :key="i"
                    class="replacement-row"
                  >
                    <div class="rt-field rt-field-qty">
                      <DxSelectBox
                        :key="
                          'all-cur-' +
                          i +
                          '-' +
                          (row.currentUnitQty !== null
                            ? row.currentUnitQty
                            : 'x')
                        "
                        :value="row.currentUnitQty"
                        :data-source="allMatchedQtys"
                        :show-clear-button="true"
                        placeholder="Any qty"
                        :disabled="isReadOnly"
                        @value-changed="
                          (e) => {
                            if (e && e.event) row.currentUnitQty = e.value;
                          }
                        "
                        :search-enabled="true"
                      />
                    </div>
                    <div class="rt-field rt-field-qty">
                      <DxNumberBox
                        :value="row.newUnitQty"
                        :show-spin-buttons="false"
                        :disabled="isReadOnly"
                        @value-changed="row.newUnitQty = $event.value"
                        :format="'###,###,##0.0000'"
                        :key="'all-new-' + i"
                      />
                      <span
                        v-if="
                          validated && rowErrors[i] && rowErrors[i].newUnitQty
                        "
                        class="param-error"
                      >
                        {{ rowErrors[i].newUnitQty }}
                      </span>
                    </div>
                    <DxButton
                      :key="'all-del-' + i"
                      v-if="!isReadOnly"
                      icon="trash"
                      type="normal"
                      styling-mode="text"
                      class="rt-del-btn"
                      :disabled="qtyAllRows.length === 1"
                      @click="removeQtyAllRow(i)"
                    />
                  </div>
                  <DxButton
                    v-if="!isReadOnly"
                    text="+ Add Row"
                    type="normal"
                    styling-mode="outlined"
                    class="add-row-btn"
                    @click="addQtyAllRow"
                  />
                </template>

                <!-- UpdateQtyNominated — per-row MaterialCode -->
                <template v-if="op.value === 'UpdateQtyNominated'">
                  <div
                    v-if="qtyNominatedRows.length > 0"
                    class="replacement-header"
                  >
                    <span class="rt-col-code"
                      >Material Code
                      <span class="required-mark">*</span></span
                    >
                    <span class="rt-col-qty"
                      >Current Unit Qty
                      <span class="qty-optional"
                        >(optional — exact match)</span
                      ></span
                    >
                    <span class="rt-col-qty"
                      >New Unit Qty
                      <span class="required-mark">*</span></span
                    >
                    <span class="rt-col-del"></span>
                  </div>
                  <div
                    v-for="(row, i) in qtyNominatedRows"
                    :key="i"
                    class="replacement-row"
                  >
                    <div class="rt-field">
                      <DxSelectBox
                        :key="
                          'nom-code-' + i + '-' + (row.materialCode || 'x')
                        "
                        :value="row.materialCode"
                        :data-source="matchedMaterialsList"
                        display-expr="Code"
                        value-expr="Code"
                        :search-enabled="true"
                        :search-expr="['Code', 'Description']"
                        :drop-down-options="{ width: 300 }"
                        placeholder="Material code..."
                        :show-clear-button="true"
                        :disabled="isReadOnly"
                        @value-changed="
                          (e) => {
                            if (e && e.event) row.materialCode = e.value;
                          }
                        "
                        item-template="nom-material-item"
                      >
                        <template #nom-material-item="{ data }">
                          <div class="custom-auto-complete">
                            <span class="auto-complete-primary">{{
                              data.Code
                            }}</span>
                            <span class="auto-complete-secondary">{{
                              data.Description
                            }}</span>
                          </div>
                        </template>
                      </DxSelectBox>
                      <span
                        v-if="
                          validated &&
                          rowErrors[i] &&
                          rowErrors[i].materialCode
                        "
                        class="param-error"
                        >{{ rowErrors[i].materialCode }}</span
                      >
                    </div>
                    <div class="rt-field rt-field-qty">
                      <DxSelectBox
                        :key="
                          'nom-cur-' + i + '-' + (row.materialCode || 'x')
                        "
                        :value="row.currentUnitQty"
                        :data-source="getNominatedQtys(row.materialCode)"
                        :show-clear-button="true"
                        placeholder="Any qty"
                        :disabled="isReadOnly"
                        @value-changed="
                          (e) => {
                            if (e && e.event) row.currentUnitQty = e.value;
                          }
                        "
                        :search-enabled="true"
                      />
                    </div>
                    <div class="rt-field rt-field-qty">
                      <DxNumberBox
                        :value="row.newUnitQty"
                        :show-spin-buttons="false"
                        :disabled="isReadOnly"
                        @value-changed="row.newUnitQty = $event.value"
                        :format="'###,###,##0.0000'"
                        :key="'nom-new-' + i"
                      />
                      <span
                        v-if="
                          validated && rowErrors[i] && rowErrors[i].newUnitQty
                        "
                        class="param-error"
                      >
                        {{ rowErrors[i].newUnitQty }}
                      </span>
                    </div>
                    <DxButton
                      :key="'nom-del-' + i"
                      v-if="!isReadOnly"
                      icon="trash"
                      type="normal"
                      styling-mode="text"
                      class="rt-del-btn"
                      :disabled="qtyNominatedRows.length === 1"
                      @click="removeQtyNominatedRow(i)"
                    />
                  </div>
                  <DxButton
                    v-if="!isReadOnly"
                    text="+ Add Row"
                    type="normal"
                    styling-mode="outlined"
                    class="add-row-btn"
                    @click="addQtyNominatedRow"
                  />
                </template>

                <!-- ReplaceMaterial / ReplaceMaterialAndQty -->
                <template
                  v-if="
                    op.value === 'ReplaceMaterial' ||
                    op.value === 'ReplaceMaterialAndQty'
                  "
                >
                  <div v-if="replacements.length > 0" class="replacement-header">
                    <span class="rt-col-code"
                      >Old Material Code
                      <span class="required-mark">*</span></span
                    >
                    <span class="rt-col-code"
                      >New Material Code
                      <span class="required-mark">*</span></span
                    >
                    <span
                      v-if="op.value === 'ReplaceMaterialAndQty'"
                      class="rt-col-qty"
                      >New Unit Qty
                      <span class="required-mark">*</span></span
                    >
                    <span class="rt-col-del"></span>
                  </div>
                  <div
                    v-for="(row, i) in replacements"
                    :key="i"
                    class="replacement-row"
                  >
                    <div class="rt-field">
                      <DxSelectBox
                        :key="'old-' + i + '-' + (row.OldCode || 'x')"
                        :value="row.OldCode"
                        :data-source="matchedMaterialsList"
                        display-expr="Code"
                        value-expr="Code"
                        :search-enabled="true"
                        :search-expr="['Code', 'Description']"
                        :drop-down-options="{ width: 300 }"
                        placeholder="Old code..."
                        :show-clear-button="true"
                        :disabled="isReadOnly"
                        @value-changed="onOldCodeChange($event, row)"
                        item-template="old-material-item"
                      >
                        <template #old-material-item="{ data }">
                          <div class="custom-auto-complete">
                            <span class="auto-complete-primary">{{
                              data.Code
                            }}</span>
                            <span class="auto-complete-secondary">{{
                              data.Description
                            }}</span>
                          </div>
                        </template>
                      </DxSelectBox>
                      <span
                        v-if="validated && !row.OldCode"
                        class="param-error"
                        >Old code is required.</span
                      >
                    </div>
                    <div class="rt-field">
                      <DxSelectBox
                        :key="'new-' + i + '-' + (row.NewCode || 'x')"
                        :value="row.NewCode"
                        :data-source="getNewCodeDataSource(row.OldCode)"
                        display-expr="Code"
                        value-expr="Code"
                        :search-enabled="true"
                        :search-expr="['Code', 'Description1']"
                        :drop-down-options="{ width: 300 }"
                        placeholder="New code..."
                        :show-clear-button="true"
                        :disabled="isReadOnly"
                        @value-changed="
                          (e) => {
                            if (e && e.event) row.NewCode = e.value;
                          }
                        "
                        item-template="new-material-item"
                      >
                        <template #new-material-item="{ data }">
                          <div class="custom-auto-complete">
                            <span class="auto-complete-primary">{{
                              data.Code
                            }}</span>
                            <span class="auto-complete-secondary">{{
                              data.Description1
                            }}</span>
                          </div>
                        </template>
                      </DxSelectBox>
                      <span
                        v-if="validated && !row.NewCode"
                        class="param-error"
                        >New code is required.</span
                      >
                    </div>
                    <div
                      v-if="op.value === 'ReplaceMaterialAndQty'"
                      class="rt-field rt-field-qty"
                    >
                      <DxNumberBox
                        :value="row.NewQty"
                        :show-spin-buttons="false"
                        :disabled="isReadOnly"
                        @value-changed="row.NewQty = $event.value"
                        :format="'###,###,##0.0000'"
                      />
                      <span
                        v-if="validated && row.NewQty == null"
                        class="param-error"
                        >Required.</span
                      >
                    </div>
                    <DxButton
                      v-if="!isReadOnly"
                      :key="'rep-del-' + i"
                      icon="trash"
                      type="normal"
                      styling-mode="text"
                      class="rt-del-btn"
                      @click="removeReplacementRow(i)"
                    />
                  </div>
                  <div
                    v-if="validated && replacements.length === 0"
                    class="param-error"
                  >
                    At least one replacement row is required.
                  </div>
                  <DxButton
                    v-if="!isReadOnly"
                    text="+ Add Row"
                    type="normal"
                    styling-mode="outlined"
                    class="add-row-btn"
                    @click="addReplacementRow"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DxButton from "devextreme-vue/button";
import DxSelectBox from "devextreme-vue/select-box";
import { DxNumberBox } from "devextreme-vue/number-box";
import ApiService from "@/_services/api/api.service";
import { custom as dxCustomDialog } from "devextreme/ui/dialog";
import { formatNumber } from "devextreme/localization";

export default {
  name: "GcWoBulkUpdateMaterialConfig",
  components: { DxButton, DxSelectBox, DxNumberBox },
  props: {
    value: { type: Object, default: null },
    matchedWoList: { type: Array, default: function() { return []; } },
    isReadOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedOperation: null,
      opListKey: 0,
      qtyAllRows: [{ currentUnitQty: null, newUnitQty: null }],
      qtyNominatedRows: [{ materialCode: null, currentUnitQty: null, newUnitQty: null }],
      replacements: [],
      validated: false,
      materialOperations: [
        {
          value: "UpdateQtyAll",
          label: "Update Qty — all matched materials",
          description:
            "Sets a new unit quantity on every material line matched by the search criteria across all included work orders.",
        },
        {
          value: "UpdateQtyNominated",
          label: "Update Qty — nominated material codes",
          description:
            "Sets new unit quantities for one or more specific material codes from the matched set.",
        },
        {
          value: "ReplaceMaterial",
          label: "Replace material",
          description:
            "Replaces one or more material codes. Existing quantities are preserved.",
        },
        {
          value: "ReplaceMaterialAndQty",
          label: "Replace material and update qty",
          description:
            "Replaces one or more material codes and sets a new unit quantity for each replacement line.",
        },
      ],
    };
  },
  computed: {
    quantityRange() {
      if (!this.matchedWoList || !this.matchedWoList.length) return null;
      var min = Infinity;
      var max = -Infinity;
      var sum = 0;
      var count = 0;
      for (var i = 0; i < this.matchedWoList.length; i++) {
        var mats = this.matchedWoList[i].MatchedMaterials || [];
        for (var j = 0; j < mats.length; j++) {
          var qty = mats[j].Qty != null ? mats[j].Qty : null;
          if (qty != null) {
            min = Math.min(min, qty);
            max = Math.max(max, qty);
            sum += qty;
            count++;
          }
        }
      }
      if (!count) return null;
      return {
        min: min,
        max: max,
        avg: Math.round((sum / count) * 10000) / 10000,
      };
    },
    allMatchedQtys() {
      if (!this.matchedWoList) return [];
      var seen = {};
      var qtys = [];
      for (var i = 0; i < this.matchedWoList.length; i++) {
        var mats = this.matchedWoList[i].MatchedMaterials || [];
        for (var j = 0; j < mats.length; j++) {
          var qty = mats[j].Qty != null ? mats[j].Qty : null;
          if (qty != null && !seen[qty]) {
            seen[qty] = true;
            qtys.push(qty);
          }
        }
      }
      return qtys.sort(function(a, b) {
        return a - b;
      });
    },
    matchedMaterialsList() {
      if (!this.matchedWoList || !this.matchedWoList.length) return [];
      var seen = {};
      var result = [];
      for (var i = 0; i < this.matchedWoList.length; i++) {
        var mats = this.matchedWoList[i].MatchedMaterials || [];
        for (var j = 0; j < mats.length; j++) {
          var m = mats[j];
          var code = m.Code || null;
          if (code && !seen[code]) {
            seen[code] = true;
            result.push({
              Code: code,
              Description: m.Description || "",
            });
          }
        }
      }
      return result.sort(function(a, b) {
        return a.Code.localeCompare(b.Code);
      });
    },
    rowErrors() {
      var op = this.selectedOperation;
      var result = {};
      if (op === "UpdateQtyAll") {
        for (var i = 0; i < this.qtyAllRows.length; i++) {
          result[i] = {};
          if (this.qtyAllRows[i].newUnitQty == null) {
            result[i].newUnitQty = "New Unit Qty is required.";
          }
        }
      }
      if (op === "UpdateQtyNominated") {
        for (var k = 0; k < this.qtyNominatedRows.length; k++) {
          result[k] = {};
          if (!this.qtyNominatedRows[k].materialCode) {
            result[k].materialCode = "Material code is required.";
          }
          if (this.qtyNominatedRows[k].newUnitQty == null) {
            result[k].newUnitQty = "New Unit Qty is required.";
          }
        }
      }
      return result;
    },
    isValid() {
      return this.selectedOperation != null && this.buildErrors().length === 0;
    },
    hasExistingConfig() {
      return (
        this.qtyAllRows.some(function(r) {
          return r.newUnitQty != null;
        }) ||
        this.qtyNominatedRows.some(function(r) {
          return r.materialCode != null || r.newUnitQty != null;
        }) ||
        this.replacements.length > 0
      );
    },
  },
  watch: {
    qtyAllRows: {
      deep: true,
      handler: function() {
        this.emitChange();
      },
    },
    qtyNominatedRows: {
      deep: true,
      handler: function() {
        this.emitChange();
      },
    },
    replacements: {
      deep: true,
      handler: function() {
        this.emitChange();
      },
    },
    selectedOperation: function() {
      this.emitChange();
    },
    value: function(newVal) {
      var incoming = JSON.stringify(newVal);
      var current = JSON.stringify(this.buildCurrentConfig());
      if (incoming !== current) {
        this.restoreFromValue(newVal);
      }
    },
  },
  created() {
    this._newCodeDsCache = new Map();
    if (this.value) {
      this.restoreFromValue(this.value);
    }
  },
  methods: {
    getFormattedQty(value) {
      return formatNumber(value, "##,###,###,###,##0.0000");
    },
    getNominatedQtys(code) {
      if (!code || !this.matchedWoList) return [];
      var seen = {};
      var qtys = [];
      for (var i = 0; i < this.matchedWoList.length; i++) {
        var mats = this.matchedWoList[i].MatchedMaterials || [];
        for (var j = 0; j < mats.length; j++) {
          var m = mats[j];
          if (m.Code !== code) continue;
          var qty = m.Qty != null ? m.Qty : null;
          if (qty != null && !seen[qty]) {
            seen[qty] = true;
            qtys.push(qty);
          }
        }
      }
      return qtys.sort(function(a, b) {
        return a - b;
      });
    },
    getNewCodeDataSource(excludeCode) {
      if (excludeCode) {
        if (!this._newCodeDsCache.has(excludeCode)) {
          this._newCodeDsCache.set(
            excludeCode,
            ApiService.createDataSource("api/Product", {
              select: ["Code", "Description1", "Inactive"],
              filter: [
                ["Code", "<>", excludeCode],
                "and",
                ["Inactive", "=", "N"],
              ],
            })
          );
        }
        return this._newCodeDsCache.get(excludeCode);
      }
      if (!this._defaultNewCodeDs) {
        this._defaultNewCodeDs = ApiService.createDataSource("api/Product", {
          select: ["Code", "Description1", "Inactive"],
          filter: [["Inactive", "=", "N"]],
        });
      }
      return this._defaultNewCodeDs;
    },
    onOldCodeChange(e, row) {
      if (!e || !e.event) return;
      if (row.OldCode !== e.value) {
        row.OldCode = e.value;
        row.NewCode = null;
      }
    },
    async onOperationChange(newOp) {
      if (newOp === this.selectedOperation) return;
      if (this.hasExistingConfig) {
        var confirmed = await dxCustomDialog({
          title: "Change Operation",
          messageHtml:
            "Switching operation will clear your current configuration. Do you want to continue?",
          buttons: [
            {
              text: "Keep Current",
              onClick: function() {
                return false;
              },
            },
            {
              text: "Continue",
              type: "default",
              stylingMode: "contained",
              onClick: function() {
                return true;
              },
            },
          ],
        }).show();
        if (!confirmed) {
          this.opListKey++;
          return;
        }
        this.clearParams();
      }
      this.selectedOperation = newOp;
    },
    clearOperation() {
      this.selectedOperation = null;
      this.clearParams();
    },
    clearParams() {
      this.qtyAllRows = [{ currentUnitQty: null, newUnitQty: null }];
      this.qtyNominatedRows = [{ materialCode: null, currentUnitQty: null, newUnitQty: null }];
      this.replacements = [];
    },
    addQtyAllRow() {
      this.qtyAllRows.push({ currentUnitQty: null, newUnitQty: null });
    },
    removeQtyAllRow(index) {
      if (this.qtyAllRows.length > 1) this.qtyAllRows.splice(index, 1);
    },
    addQtyNominatedRow() {
      this.qtyNominatedRows.push({ materialCode: null, currentUnitQty: null, newUnitQty: null });
    },
    removeQtyNominatedRow(index) {
      if (this.qtyNominatedRows.length > 1)
        this.qtyNominatedRows.splice(index, 1);
    },
    addReplacementRow() {
      this.replacements.push({
        OldCode: null,
        NewCode: null,
        NewQty: null,
      });
    },
    removeReplacementRow(index) {
      this.replacements.splice(index, 1);
    },
    buildErrors() {
      var op = this.selectedOperation;
      var errors = [];
      if (!op) {
        errors.push("Please select a material update operation.");
        return errors;
      }
      if (op === "UpdateQtyAll") {
        if (this.qtyAllRows.length === 0) {
          errors.push("At least one row is required.");
        } else {
          for (var i = 0; i < this.qtyAllRows.length; i++) {
            if (this.qtyAllRows[i].newUnitQty == null) {
              errors.push("Row " + (i + 1) + ": New Unit Qty is required.");
            }
          }
        }
      }
      if (op === "UpdateQtyNominated") {
        if (this.qtyNominatedRows.length === 0) {
          errors.push("At least one row is required.");
        } else {
          for (var k = 0; k < this.qtyNominatedRows.length; k++) {
            var nr = this.qtyNominatedRows[k];
            if (!nr.materialCode) {
              errors.push("Row " + (k + 1) + ": Material code is required.");
            }
            if (nr.newUnitQty == null) {
              errors.push("Row " + (k + 1) + ": New Unit Qty is required.");
            }
          }
        }
      }
      if (op === "ReplaceMaterial" || op === "ReplaceMaterialAndQty") {
        if (this.replacements.length === 0) {
          errors.push("At least one replacement row is required.");
        }
        for (var r = 0; r < this.replacements.length; r++) {
          var row = this.replacements[r];
          if (!row.OldCode)
            errors.push("Row " + (r + 1) + ": Old code is required.");
          if (!row.NewCode)
            errors.push("Row " + (r + 1) + ": New code is required.");
          if (op === "ReplaceMaterialAndQty" && row.NewQty == null) {
            errors.push("Row " + (r + 1) + ": New Unit Qty is required.");
          }
        }
      }
      return errors;
    },
    buildCurrentConfig() {
      var op = this.selectedOperation;
      if (!op) return null;
      if (op === "UpdateQtyAll") {
        return {
          Operation: "UpdateQtyAll",
          Params: {
            QtyUpdates: this.qtyAllRows.map(function(row) {
              var item = { NewQty: row.newUnitQty };
              if (row.currentUnitQty !== null && row.currentUnitQty !== undefined && row.currentUnitQty !== "") {
                item.CurrentQty = row.currentUnitQty;
              }
              return item;
            }),
          },
        };
      }
      if (op === "UpdateQtyNominated") {
        return {
          Operation: "UpdateQtyNominated",
          Params: {
            QtyUpdates: this.qtyNominatedRows.map(function(row) {
              var item = { MaterialCode: row.materialCode, NewQty: row.newUnitQty };
              if (row.currentUnitQty !== null && row.currentUnitQty !== undefined && row.currentUnitQty !== "") {
                item.CurrentQty = row.currentUnitQty;
              }
              return item;
            }),
          },
        };
      }
      if (op === "ReplaceMaterial") {
        return {
          Operation: "ReplaceMaterial",
          Params: {
            Replacements: this.replacements.map(function(r) {
              return { OldCode: r.OldCode, NewCode: r.NewCode };
            }),
          },
        };
      }
      if (op === "ReplaceMaterialAndQty") {
        return {
          Operation: "ReplaceMaterialAndQty",
          Params: {
            Replacements: this.replacements.map(function(r) {
              return { OldCode: r.OldCode, NewCode: r.NewCode, NewQty: r.NewQty };
            }),
          },
        };
      }
      return null;
    },
    restoreFromValue(val) {
      if (!val) {
        this.selectedOperation = null;
        this.clearParams();
        return;
      }
      this.selectedOperation = val.Operation || null;
      var params = val.Params || {};
      if (val.Operation === "UpdateQtyAll") {
        var allUpdates = params.QtyUpdates || [];
        this.qtyAllRows = allUpdates.length
          ? allUpdates.map(function(u) {
              return {
                currentUnitQty: u.CurrentQty !== undefined ? u.CurrentQty : null,
                newUnitQty: u.NewQty !== undefined ? u.NewQty : null,
              };
            })
          : [{ currentUnitQty: null, newUnitQty: null }];
        this.qtyNominatedRows = [{ materialCode: null, currentUnitQty: null, newUnitQty: null }];
        this.replacements = [];
      } else if (val.Operation === "UpdateQtyNominated") {
        var nomUpdates = params.QtyUpdates || [];
        this.qtyNominatedRows = nomUpdates.length
          ? nomUpdates.map(function(u) {
              return {
                materialCode: u.MaterialCode || null,
                currentUnitQty: u.CurrentQty !== undefined ? u.CurrentQty : null,
                newUnitQty: u.NewQty !== undefined ? u.NewQty : null,
              };
            })
          : [{ materialCode: null, currentUnitQty: null, newUnitQty: null }];
        this.qtyAllRows = [{ currentUnitQty: null, newUnitQty: null }];
        this.replacements = [];
      } else if (
        val.Operation === "ReplaceMaterial" ||
        val.Operation === "ReplaceMaterialAndQty"
      ) {
        var reps = params.Replacements || [];
        this.replacements = reps.map(function(r) {
          return {
            OldCode: r.OldCode || null,
            NewCode: r.NewCode || null,
            NewQty: r.NewQty !== undefined ? r.NewQty : null,
          };
        });
        this.qtyAllRows = [{ currentUnitQty: null, newUnitQty: null }];
        this.qtyNominatedRows = [{ materialCode: null, currentUnitQty: null, newUnitQty: null }];
      }
      this.validated = true;
    },
    validate() {
      this.validated = true;
      return this.isValid;
    },
    emitChange() {
      var config = this.buildCurrentConfig();
      this.$emit("config-change", config);
      this.$emit("validation-change", { isValid: this.isValid });
    },
  },
};
</script>

<style lang="scss">
.gc-wo-bulk-update-material-config {
  .config-card {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
  }

  .config-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #b9e5fb;
    border-radius: 6px 6px 0 0;
    background: #e0f2fe;
  }

  .config-card-title {
    font-size: 13px;
    font-weight: 600;
    color: #344054;
  }

  .config-card-title-secondary {
    font-size: 12px;
    color: #0369a1;
  }

  .config-card-body {
    padding: 16px;
  }

  .param-error-top {
    display: block;
    margin-bottom: 10px;
  }

  // ── Operation section ──────────────────────────────────────────────────────
  .operation-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .operation-section-header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-bottom: 8px;
    border-bottom: 1px solid #dee2e6;

    > .dx-button .dx-button-content {
      display: flex;
      align-items: center;
    }
  }

  .operation-clear-btn {
    font-size: 12px;
    color: #6c757d;

    &:hover {
      color: #dc3545;
    }
  }

  .operation-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .operation-item {
    display: flex;
    flex-direction: column;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    transition: border-color 0.15s, background 0.15s;
    overflow: hidden;

    &:hover {
      border-color: #03a9f4;
    }

    &.op-active {
      border-color: #03a9f4;
      background: #e8f6fe;
    }
  }

  .op-header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    cursor: pointer;

    .op-radio {
      margin-top: 2px;
      flex-shrink: 0;
      accent-color: #03a9f4;
    }

    .op-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .op-label {
      font-size: 13px;
      font-weight: 500;
      color: #101828;
    }

    .op-desc {
      font-size: 12px;
      color: #6c757d;
    }
  }

  // ── Inline params panel ────────────────────────────────────────────────────
  .op-params-inline {
    border-top: 1px solid #b3e5fc;
    padding: 14px 12px 14px 34px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: #f0faff;
  }

  // ── Qty context stats ──────────────────────────────────────────────────────
  .qty-range-context {
    margin-bottom: 4px;
  }

  .qty-range-label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    color: #6c757d;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  .qty-range-stats {
    display: flex;
    gap: 8px;
  }

  .qty-stat-box {
    display: flex;
    flex-direction: row;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    background: #f8f9fa;
    min-width: 80px;
    align-items: center;
  }

  .qty-stat-key {
    font-size: 10px;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .qty-stat-val {
    font-size: 12px;
    font-weight: 600;
    color: #101828;
  }

  // ── Param row (legacy — kept for layout compatibility) ─────────────────────
  .param-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-width: 320px;
  }

  .param-label {
    font-size: 12px;
    font-weight: 500;
    color: #344054;
  }

  .required-mark {
    color: #dc3545;
    margin-left: 2px;
  }

  .qty-optional {
    font-size: 10px;
    color: #6c757d;
    font-weight: 400;
  }

  // ── Replacement table ──────────────────────────────────────────────────────
  .replacement-header {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 4px;

    .rt-col-code {
      width: 250px;
      font-size: 11px;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }

    .rt-col-qty {
      width: 150px;
      font-size: 11px;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }

    .rt-col-del {
      width: 32px;
    }
  }

  .replacement-row {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin-bottom: 6px;

    .rt-del-btn {
      flex-shrink: 0;
      margin-top: 6px;
      color: #6c757d;

      &:hover {
        color: #dc3545;
      }
    }
  }

  .rt-field {
    width: 250px;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .dx-selectbox,
    .dx-numberbox {
      width: 100%;
    }

    &.rt-field-qty {
      flex: 0 0 150px;
      width: 150px;
    }
  }

  .add-row-btn {
    margin-top: 4px;
    font-size: 13px;
  }

  // ── Validation errors ──────────────────────────────────────────────────────
  .param-error {
    display: block;
    font-size: 12px;
    color: #dc3545;
    margin-top: 2px;
  }

  // ── Autocomplete item ──────────────────────────────────────────────────────
  .custom-auto-complete {
    display: flex;
    flex-direction: column;
    padding: 2px 0;

    .auto-complete-primary {
      font-size: 13px;
      font-weight: 500;
      color: #101828;
    }

    .auto-complete-secondary {
      font-size: 11px;
      color: #6c757d;
    }
  }
}
</style>
